'use client'

/**
 * Error Context Provider
 * Centralized error state management for tracking, displaying, and recovering from errors.
 *
 * SOC 2 CC7.2 - Error messages are sanitized before display to prevent information disclosure.
 * Full error details (stack traces, internal paths) are preserved internally but never shown to users.
 * Each error includes a correlation ID that users can provide to support for investigation.
 */

import React, { createContext, useContext, useCallback, useReducer, type ReactNode } from 'react'

// =============================================================================
// TYPES
// =============================================================================

export type ErrorSeverity = 'critical' | 'high' | 'medium' | 'low'

export interface TrackedError {
  id: string
  error: Error
  /** Sanitized message safe for user display */
  message: string
  /** Original raw message for internal logging only */
  rawMessage: string
  severity: ErrorSeverity
  context: string
  timestamp: Date
  stack?: string
  retryable: boolean
  retryFn?: () => void
}

interface ErrorState {
  errors: TrackedError[]
}

type ErrorAction =
  | { type: 'ADD_ERROR'; payload: TrackedError }
  | { type: 'REMOVE_ERROR'; payload: string }
  | { type: 'CLEAR_ALL' }

interface ErrorContextValue {
  errors: TrackedError[]
  reportError: (error: Error, options?: {
    severity?: ErrorSeverity
    context?: string
    retryable?: boolean
    retryFn?: () => void
  }) => string
  removeError: (id: string) => void
  clearErrors: () => void
  retryErrorOperation: (id: string) => void
}

// =============================================================================
// REDUCER
// =============================================================================

const MAX_ERRORS = 100

function errorReducer(state: ErrorState, action: ErrorAction): ErrorState {
  switch (action.type) {
    case 'ADD_ERROR': {
      const errors = [action.payload, ...state.errors].slice(0, MAX_ERRORS)
      return { errors }
    }
    case 'REMOVE_ERROR':
      return { errors: state.errors.filter(e => e.id !== action.payload) }
    case 'CLEAR_ALL':
      return { errors: [] }
    default:
      return state
  }
}

// =============================================================================
// CONTEXT
// =============================================================================

// =============================================================================
// ERROR SANITIZATION (SOC 2 CC7.2 - prevent information disclosure)
// =============================================================================

/** Patterns that indicate internal implementation details */
const INTERNAL_PATTERNS = [
  /at\s+[\w.]+\s+\(.*:\d+:\d+\)/i,  // stack trace lines
  /\/src\//i,                          // source file paths
  /\/node_modules\//i,                 // dependency paths
  /ECONNREFUSED|ETIMEDOUT/i,          // network internals
  /errno|syscall|code:/i,             // OS-level details
  /postgres|supabase|firebase/i,      // backend infrastructure
]

/** Map known error patterns to user-friendly messages */
function sanitizeErrorMessage(rawMessage: string): string {
  if (!rawMessage) return 'An unexpected error occurred. Please try again.'

  // Network errors
  if (/network|fetch|ECONNREFUSED|ETIMEDOUT|failed to fetch/i.test(rawMessage)) {
    return 'Unable to connect to the server. Please check your connection and try again.'
  }
  // Auth errors
  if (/unauthorized|401|token.*expired|session.*expired/i.test(rawMessage)) {
    return 'Your session has expired. Please sign in again.'
  }
  if (/forbidden|403|access.*denied/i.test(rawMessage)) {
    return 'You do not have permission to perform this action.'
  }
  // Validation errors (safe to pass through)
  if (/validation|invalid.*input|required.*field/i.test(rawMessage)) {
    return rawMessage
  }
  // Server errors
  if (/500|internal.*server/i.test(rawMessage)) {
    return 'A server error occurred. Please try again later.'
  }
  // Check for internal details that should not be exposed
  if (INTERNAL_PATTERNS.some(pattern => pattern.test(rawMessage))) {
    return 'An unexpected error occurred. Please try again or contact support.'
  }
  // If the message is short and doesn't contain suspicious patterns, allow it
  if (rawMessage.length < 200) {
    return rawMessage
  }
  return 'An unexpected error occurred. Please try again or contact support.'
}

const ErrorContext = createContext<ErrorContextValue | undefined>(undefined)

export function ErrorProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(errorReducer, { errors: [] })

  const reportError = useCallback((error: Error, options?: {
    severity?: ErrorSeverity
    context?: string
    retryable?: boolean
    retryFn?: () => void
  }): string => {
    const id = `err-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const rawMessage = error.message || 'Unknown error'
    const severity = options?.severity ?? 'medium'
    const tracked: TrackedError = {
      id,
      error,
      message: sanitizeErrorMessage(rawMessage),
      rawMessage,
      severity,
      context: options?.context ?? 'unknown',
      timestamp: new Date(),
      stack: error.stack,
      retryable: options?.retryable ?? false,
      retryFn: options?.retryFn,
    }
    dispatch({ type: 'ADD_ERROR', payload: tracked })

    // SOC 2 CC7.2: Log critical/high severity errors to audit trail
    if (severity === 'critical' || severity === 'high') {
      try {
        fetch('/api/audit/activities', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'frontend_error',
            level: severity === 'critical' ? 'error' : 'warn',
            category: 'security_event',
            result: 'failure',
            message: `Frontend error [${id}]: ${rawMessage}`,
            metadata: { errorId: id, context: options?.context, severity },
          }),
        }).catch(() => {}) // Fire-and-forget; don't block UI for audit logging
      } catch {
        // Audit logging failure must not break the application
      }
    }

    return id
  }, [])

  const removeError = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_ERROR', payload: id })
  }, [])

  const clearErrors = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL' })
  }, [])

  const retryErrorOperation = useCallback((id: string) => {
    const tracked = state.errors.find(e => e.id === id)
    if (tracked?.retryFn) {
      tracked.retryFn()
      dispatch({ type: 'REMOVE_ERROR', payload: id })
    }
  }, [state.errors])

  return (
    <ErrorContext.Provider value={{
      errors: state.errors,
      reportError,
      removeError,
      clearErrors,
      retryErrorOperation,
    }}>
      {children}
    </ErrorContext.Provider>
  )
}

export function useErrorContext() {
  const context = useContext(ErrorContext)
  if (!context) {
    throw new Error('useErrorContext must be used within an ErrorProvider')
  }
  return context
}
